import { useCallback, useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { bundleInfo } from "../lib/appVersion";
import { parseRelease, readWhatsNew, recentVersions, versionsToShow } from "../lib/whatsNew";

// Sent by the version number (foot of the sidebar) to reopen the panel by hand.
export const WHATS_NEW_OPEN_EVENT = "jamora:whats-new-open";

async function loadWhatsNew() {
  try {
    const response = await fetch("/whats-new.json", { cache: "no-store", credentials: "same-origin" });
    return response.ok ? readWhatsNew(await response.json()) : null;
  } catch {
    return null; // offline or file missing: the panel stays silent, never an error
  }
}

// « Nouveautés » panel (lot S5): on the first sign-in after a minor or major version, lists what changed since the last version the person
// saw (kept on their account: `last_seen_version`, read from `GET /auth/me`). Never blocking: any failure (offline, file missing, unreadable
// answer) leaves the panel closed; only active on a real published version (never on « dev »).
export default function useWhatsNew() {
  const { user } = useAuth() || {};
  const current = bundleInfo().version;
  const userId = user && user.id ? user.id : null;
  const enabled = Boolean(userId) && parseRelease(current) !== null;
  const lastSeen = (user && user.last_seen_version) || "";
  const [versions, setVersions] = useState([]);
  const [open, setOpen] = useState(false);
  const checked = useRef(null);

  const markSeen = useCallback(async () => {
    try {
      await api.post("/auth/me/last-seen-version", { version: current });
    } catch {
      /* harmless: the person will see the panel again at the next sign-in */
    }
  }, [current]);

  useEffect(() => {
    if (!enabled || checked.current === userId) return undefined;
    checked.current = userId;
    let cancelled = false;
    (async () => {
      if (!lastSeen) {
        await markSeen(); // first sign-in: no history shown, we start from the running version
        return;
      }
      const data = await loadWhatsNew();
      const toShow = data ? versionsToShow(data, lastSeen, current) : [];
      if (cancelled) return;
      if (toShow.length > 0) {
        setVersions(toShow);
        setOpen(true);
      } else if (data && parseRelease(lastSeen) && lastSeen !== current) {
        await markSeen(); // only fixes: nothing to show, but the version counts as seen
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, userId, lastSeen, current, markSeen]);

  useEffect(() => {
    if (!enabled) return undefined;
    const reopen = async () => {
      const data = await loadWhatsNew();
      const recent = data ? recentVersions(data, current) : [];
      if (recent.length > 0) {
        setVersions(recent);
        setOpen(true);
      }
    };
    window.addEventListener(WHATS_NEW_OPEN_EVENT, reopen);
    return () => window.removeEventListener(WHATS_NEW_OPEN_EVENT, reopen);
  }, [enabled, current]);

  const close = useCallback(() => {
    setOpen(false);
    void markSeen();
  }, [markSeen]);

  return { open, versions, close };
}
